/*
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */

import "@reach/dialog/styles.css";
import "src/styles/fonts.css";
import mixpanel from "mixpanel-browser";
import { wrapWithMixpanel } from "src/utils/mixpanel/MixpanelContext";
import { wrapWithWindowResizeContext } from "src/utils/providers/WindowResizeContextProvider";
import { wrapWithProviders } from "src/utils/providers/wrapWithProviders";
import { GatsbyBrowser } from "gatsby";
import { ApplyMixpanelHook } from "src/utils/mixpanel/ApplyMixpanelHook";
import ApplyLocaleRedirectHook from "src/i18n/ApplyLocaleRedirectHook";
import ThirdPartyScripts from "src/utils/thirdPartyScripts/ThirdPartyScripts";
import { wrapWithSegment } from "src/utils/segment/SegmentContext";
import withPrefixSafe from "src/utils/withPrefixSafe";
import { PageTracker } from "src/utils/segment/PageTracker";
import { isEligibilityFormEmbedPath } from "src/utils/eligibilityFormEmbedPath";

declare global {
  interface Window {
    dataLayer: unknown[];
    zE?: (
      action: "messenger" | "messenger:on" | "messenger:set",
      command: string,
      ...args: unknown[]
    ) => void;
    OnetrustActiveGroups?: string;
    __turnstileLoaded?: boolean;
    onTurnstileLoad?: () => void;
    __onTurnstileReadyInternal?: () => void;
    turnstile?: {
      render(
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          "response-field"?: boolean;
          appearance?: "always" | "execute" | "interaction-only";
          execution?: "render" | "execute";
        },
      ): string;
      execute?(widgetId: string): void;
      reset(widgetIdOrContainer?: string | HTMLElement): void;
      remove(widgetId: string): void;
    };
  }
}

// All providers and wrapping elements needed by each page should be added to this list.
// Each provider must be passed as a function rather than using the provider directly to avoid defining initialization twice.
export const wrapRootElement: GatsbyBrowser["wrapRootElement"] =
  wrapWithProviders([
    wrapWithSegment(process.env.GATSBY_SEGMENT_WRITE_KEY ?? ""),
    wrapWithMixpanel(
      mixpanel,
      process.env.NODE_ENV ?? "",
      process.env.GATSBY_MIXPANEL_TOKEN ?? "",
    ),
    wrapWithWindowResizeContext,
  ]);

// All pages are wrapped with a hook that sends a Page View event to MixPanel
export const wrapPageElement: GatsbyBrowser["wrapPageElement"] = ({
  element,
  props,
}) => {
  const isEmbedPage = isEligibilityFormEmbedPath(props.location.pathname);

  const pageInner = (
    <ApplyMixpanelHook props={props}>
      <ApplyLocaleRedirectHook>
        {isEmbedPage ? element : <PageTracker>{element}</PageTracker>}
      </ApplyLocaleRedirectHook>
    </ApplyMixpanelHook>
  );

  if (isEmbedPage) {
    return pageInner;
  }

  return (
    <ThirdPartyScripts isDev={process.env.GATSBY_ENV === "dev"}>
      {pageInner}
    </ThirdPartyScripts>
  );
};

// Adapted from gatsby-plugin-google-tagmanager
export const onRouteUpdate: GatsbyBrowser["onRouteUpdate"] = () => {
  // Wrap inside a timeout to ensure the title has properly been changed
  setTimeout(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    window?.dataLayer?.push({ event: "gatsby-route-change" });
  }, 50);
};

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: { location },
}) => {
  if (location.pathname === withPrefixSafe("/for-members/support/")) {
    return false;
  }
  return true;
};

export const onInitialClientRender: GatsbyBrowser["onInitialClientRender"] =
  (): void => {
    if (window.location.pathname === withPrefixSafe("/for-members/support/")) {
      const hash = window.location.hash.substring(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView();
          }, 500);
        }
      }
    }
  };
